import type { FluxErrorEntityErrorCodes } from '../types.js';

export const DatabaseErrorCodes = {
  DatabaseNoFields: {
    code: 'database/no-fields',
    message: 'O banco de dados precisa ter pelo menos um campo configurado para ser utilizado.',
    explanation: `Esse erro ocorre quando o banco de dados **não possui campos definidos** em sua estrutura.

**Causas comuns:**
- O banco de dados foi criado mas **nenhum campo foi adicionado** ainda
- A configuração do esquema (\`schema.fields\`) foi salva como um array vazio
- Falha na migração ou importação de estrutura

**Como resolver:**
Acesse as configurações do banco de dados e adicione pelo menos um campo (ex: Nome, Data, etc) para começar a criar registros.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Criando um banco de dados',
        path: '/wiki/docs/databases/creating-database',
      },
      {
        name: 'Gerenciando Esquemas',
        path: '/wiki/docs/databases/managing-schema',
      },
    ],
  },
  DatabaseNoSchema: {
    code: 'database/no-schema',
    message: 'A configuração estrutural (esquema) do banco de dados não foi encontrada.',
    explanation: `Esse erro indica que a **definição de estrutura** (schema) do banco de dados está ausente ou corrompida.

**Causas comuns:**
- O objeto de configuração \`schema\` é **nulo ou undefined**
- Erro crítico durante a criação do banco de dados
- Inconsistência de dados ("banco de dados órfão")

**Como diagnosticar:**
Verifique se o banco de dados foi criado corretamente via API/Interface. Se o problema persistir, pode indicar dados corrompidos na tabela de definições.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Criando um banco de dados',
        path: '/wiki/docs/databases/creating-database',
      },
    ],
  },
  DatabaseNotFound: {
    code: 'database/not-found',
    message: 'O banco de dados solicitado não foi encontrado.',
    explanation: `Esse erro é retornado quando o **banco de dados solicitado não existe** ou não está acessível.

**Causas comuns:**
- O \`databaseId\` fornecido está **incorreto**
- O banco de dados foi **excluído** (soft delete ou hard delete)
- O usuário **não tem permissão** de leitura para este banco (em alguns casos retorna 404 por segurança)

**Como diagnosticar:**
Verifique se o ID está correto na URL ou payload. Confirme se o banco de dados ainda existe na listagem geral.`,
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'database'>;

export const DatabaseIdLookupErrorCodes = {
  DatabaseIdLookupDatabaseRecordId: {
    code: 'database-id-lookup/database-record-id',
    message: 'O registro solicitado não foi encontrado.',
    explanation: `Erro de integridade referencial ao tentar localizar um **ID de registro**.

**Causas comuns:**
- O \`recordId\` referenciado **não existe** na tabela de dados
- O registro foi **excluído recentemente**
- Inconsistência em índices de busca

**Como diagnosticar:**
Verifique se o ID do registro é válido e se ele realmente existe no banco de dados consultado.`,
    localizations: {},
  },
  DatabaseIdLookupDatabaseSchemaFieldId: {
    code: 'database-id-lookup/database-schema-field-id',
    message: 'O campo da estrutura do banco de dados não foi encontrado.',
    explanation: `Erro ao tentar localizar um campo específico pelo seu ID interno (\`fieldId\`).

**Causas comuns:**
- O campo foi **removido** do esquema
- O ID do campo está **incorreto** ou pertence a outro banco
- O esquema foi atualizado e o cache ainda aponta para IDs antigos

**Como resolver:**
Atualize sua referência para usar os IDs de campos listados na versão mais recente do esquema.`,
    localizations: {},
  },
  DatabaseIdLookupDatabaseSchemaId: {
    code: 'database-id-lookup/database-schema-id',
    message: 'A estrutura do banco de dados não foi encontrada.',
    explanation: `Erro interno ao tentar resolver o **ID do esquema** vinculado ao banco de dados.

**Causas comuns:**
- O banco de dados está apontando para um \`schemaId\` que não existe mais
- Inconsistência grave nos metadados do banco

**Como diagnosticar:**
Geralmente indica um problema de integridade de dados no sistema. Verifique a associação entre Banco e Esquema.`,
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'database-id-lookup'>;

export const DatabaseMemberErrorCodes = {
  DatabaseMemberAlreadyExists: {
    code: 'database-member/already-exists',
    message: 'Este usuário já é membro do banco de dados.',
    explanation: `Tentativa de adicionar um usuário que **já possui acesso** ao banco de dados.

**Causas comuns:**
- Clique duplo no botão de "Adicionar Membro"
- Retry de requisição de API sem verificação prévia
- Concorrência de operações

**Como resolver:**
Não é necessário ação corretiva, o usuário já está adicionado. Para evitar o erro, verifique se o usuário já é membro antes de enviar o comando.`,
    localizations: {},
  },
  DatabaseMemberNotFound: {
    code: 'database-member/not-found',
    message: 'O membro do banco de dados não foi encontrado.',
    explanation: `Ocorre ao tentar gerenciar (editar/remover) um membro que **não está associado** ao banco de dados.

**Causas comuns:**
- O usuário já foi **removido** anteriormente
- O \`userId\` fornecido não corresponde a nenhum membro da lista
- Tentativa de alterar permissões de um não-membro

**Como diagnosticar:**
Verifique a lista atual de membros e garanta que o ID está correto.`,
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'database-member'>;

export const DatabaseRecordErrorCodes = {
  DatabaseRecordFieldNotUnique: {
    code: 'database-record/field-not-unique',
    message:
      'Este valor já está sendo usado em outro registro. Por favor, informe um valor diferente.',
    explanation: `O valor enviado viola uma restrição de **unicidade** (\`unique: true\`) configurada no esquema.

**Causas comuns:**
- Tentativa de cadastrar um email, CPF ou código que **já existe** no banco
- Condição de corrida (race condition) onde dois registros iguais são enviados simultaneamente
- Restauração de registro antigo que conflita com um novo

**Como resolver:**
Informe um valor diferente para o campo ou verifique se o registro original deve ser atualizado em vez de criado um novo.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Gerenciando Esquemas',
        path: '/wiki/docs/databases/managing-schema',
      },
    ],
  },
  DatabaseRecordFilterByFieldUnknownField: {
    code: 'database-record/filter-by-field-unknown-field',
    message:
      'Você tentou filtrar por um campo que não existe ou foi removido deste banco de dados.',
    explanation: `A consulta de filtro faz referência a um **campo desconhecido**.

**Causas comuns:**
- O nome do campo foi digitado incorretamente no filtro
- O campo **foi removido** do esquema recentemente
- Uso de nomes de campos antigos (versões anteriores do esquema)

**Como diagnosticar:**
Verifique a lista de campos disponíveis na versão atual do banco de dados. Remova o filtro inválido da consulta.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Filtrando registros',
        path: '/wiki/docs/databases/filtering-records',
      },
    ],
  },
  DatabaseRecordIdConflict: {
    code: 'database-record/id-conflict',
    message: 'O código identificador deste registro já existe em outro item.',
    explanation: `Erro de conflito de ID ao tentar criar um registro.

**Causas comuns:**
- Tentativa de definir manualmente um \`id\` que **já está em uso**
- Falha na geração de IDs únicos (muito raro)
- Tentativa de recriar um registro exato que já existe

**Como resolver:**
Deixe o sistema gerar o ID automaticamente (recomendado) ou garanta que o ID manual seja verdadeiramente único.`,
    localizations: {},
  },
  DatabaseRecordInvalidFieldType: {
    code: 'database-record/invalid-field-type',
    message: 'O tipo de dado informado não é válido para este campo.',
    explanation: `O valor enviado não corresponde ao **tipo de dado** esperado pelo campo.

**Causas comuns:**
- Enviando texto ("123") para um campo **Number**
- Enviando texto inválido para um campo **Date**
- Enviando objeto/array para um campo primitivo (String/Boolean)

**Como resolver:**
Verifique os tipos definidos no esquema (\`String\`, \`Number\`, \`Boolean\`, \`Date\`) e converta os dados para o formato correto antes de enviar.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Tipos de Campo',
        path: '/wiki/docs/databases/field-types',
      },
    ],
  },
  DatabaseRecordMissingField: {
    code: 'database-record/missing-field',
    message: 'Por favor, preencha todos os campos obrigatórios antes de continuar.',
    explanation: `Um campo obrigatório está **ausente** ou **nulo** no payload.

**Causas comuns:**
- Campo configurado como \`required: true\` não foi enviado
- Envio de valor \`null\` ou string vazia para campo obrigatório
- Erro no formulário de frontend que não validou o preenchimento

**Como resolver:**
Garanta que todos os campos marcados como obrigatórios no esquema estejam presentes e preenchidos no payload.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Gerenciando Esquemas',
        path: '/wiki/docs/databases/managing-schema',
      },
    ],
  },
  DatabaseRecordNotFound: {
    code: 'database-record/not-found',
    message: 'O registro solicitado não foi encontrado.',
    explanation: `O registro específico não foi encontrado na tabela de dados.

**Causas comuns:**
- O \`recordId\` está **incorreto**
- O registro foi **excluído** por outro usuário
- Filtros de permissão/contexto impedem a visualização

**Como diagnosticar:**
Verifique se o ID existe.`,
    localizations: {},
  },
  DatabaseRecordUnknownField: {
    code: 'database-record/unknown-field',
    message: 'O sistema encontrou informações que não pertencem a este banco de dados.',
    explanation: `O payload contém **campos não mapeados** no esquema do banco de dados.

**Causas comuns:**
- Envio de propriedades extras no JSON
- Erro de digitação no nome do campo (case sensitive)
- Tentativa de escrever em campos de sistema (ex: metadados internos)

**Como resolver:**
Limpe o payload para enviar apenas os campos que estão definidos no esquema atual.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Gerenciando Esquemas',
        path: '/wiki/docs/databases/managing-schema',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'database-record'>;

export const DatabaseSchemaErrorCodes = {
  DatabaseSchemaFieldExists: {
    code: 'database-schema/field-exists',
    message: 'Já existe um campo com este nome. Por favor, escolha um nome diferente.',
    explanation: `Tentativa de criar ou renomear um campo para um nome que **já existe**.

**Causas comuns:**
- O banco de dados já possui um campo com o mesmo \`slug\` ou \`name\`
- Tentativa de duplicar um campo sem alterar o nome

**Como resolver:**
Escolha um nome único para o novo campo. Lembre-se que identificadores são insensíveis a maiúsculas/minúsculas em alguns contextos.`,
    localizations: {},
  },
  DatabaseSchemaFieldInternalNameForbidden: {
    code: 'database-schema/field-internal-name-forbidden',
    message: "Nomes de campo começando com 'sys_' são reservados pelo sistema. Escolha outro nome.",
    explanation: `O nome escolhido viola as **regras de nomenclatura** do sistema.

**Causas comuns:**
- Uso do prefixo \`sys_\` (reservado para colunas de controle interno)
- Tentativa de criar campos com nomes de metadados protegidos

**Como resolver:**
Escolha um nome que não comece com \`sys_\`.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Campos reservados',
        path: '/wiki/docs/databases/creating-database#configura%C3%A7%C3%A3o-de-campos',
      },
    ],
  },
  DatabaseSchemaFieldInvalidName: {
    code: 'database-schema/field-invalid-name',
    message:
      'O nome do campo contém caracteres inválidos. Use apenas letras, números e sublinhados (_).',
    explanation: `O formato do nome do campo é **inválido**.

**Causas comuns:**
- Uso de caracteres especiais (\`@\`, \`#\`, \`$\`, etc)
- Uso de espaços em branco (use \`_\` no lugar)
- Começar o nome com número (dependendo da validação específica)

**Como resolver:**
Utilize apenas letras, números e sublinhados (\`_\`) no nome técnico do campo.`,
    localizations: {},
    supportingDocs: [
      {
        name: 'Nomes de campos',
        path: '/wiki/docs/databases/creating-database#configura%C3%A7%C3%A3o-de-campos',
      },
    ],
  },
  DatabaseSchemaFieldNotFound: {
    code: 'database-schema/field-not-found',
    message: 'O campo solicitado não foi encontrado na estrutura do banco de dados.',
    explanation: `A operação referencia um campo que **não existe mais**.

**Causas comuns:**
- O campo foi **excluído** recentemente
- O \`fieldId\` ou \`name\` está incorreto na requisição
- Desincronia entre a versão do esquema no cliente e no servidor

**Como diagnosticar:**
Recarregue o esquema do banco de dados para ter a lista de campos atualizada.`,
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'database-schema'>;
